import React, { useEffect, useMemo, useState } from 'react'
import { useCurrentCloudProvider } from '@labrav/common'
import { useNavigate, useParams } from 'react-router-dom'
import { isEmpty, noop } from 'lodash'
import { useDispatch, useSelector } from '../../../store'
import type {
  OfferRecipients,
  OfferStakeholders,
} from '../../../modules/privateOffer/reducer'
import {
  getChannelPartnerPrivateOffer,
  updateChannelPartnerPrivateOffer,
} from '../../../modules/channelPartnerPrivateOffer/action'
import type { ChannelPartnerPrivateOffer } from '../../../modules/channelPartnerPrivateOffer/reducer'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Grid, Typography } from '@mui/material'
import {
  faArrowLeft,
  faTable,
  faShelves,
  faListCheck,
} from '@fortawesome/pro-regular-svg-icons'
import { useStyles } from './ViewChannelOffer.styles'
import { ShortenedNameV2, SelectedCloudIcon } from '@labrav/common'
import { OfferStatus } from '../../../modules/privateOffer/reducer'
import { ViewRecipients } from '../../../common/components/ViewRecipients/ViewRecipients'
import { ViewStakeholders } from '../../../common/components/ViewStakeholders/ViewStakeholders'
import { EditRecipients } from '../../../common/components/EditRecipients/EditRecipients'
import { EditStakeholders } from '../../../common/components/EditStakeholders/EditStakeholders'
import type { StatusOfferItem } from '../../../types/addEditProduct'
import {
  LoadingLogo,
  ModalConfirmV2,
  ModalSizeVariants,
  PlanType,
  StatusListing,
} from '@labrav/react-components'
import { ChannelOfferDetails } from './ChannelOfferDetails/ChannelOfferDetails'
import type { ReceivedResellerAuthorization } from '../../../modules/receivedResellerAuthorization/reducer'
import { getCurrentReceivedResaleAuthorization } from '../../../modules/receivedResellerAuthorization/action'
import { ChannelOfferEula } from './ChannelOfferEula/ChannelOfferEula'
import { SectionWrapper, DetailInfo } from '@labrav/common'
import { ChannelOfferHeader } from './ChannelOfferHeader/ChannelOfferHeader'
import { ChannelOfferDimensionDetails } from './ChannelOfferDimensionDetails/ChannelOfferDimensionDetails'
import { getCurrencySymbol } from '../../../common/utils/getCurrencySymbol'
import { useSyncFlyoutRouteParams } from '../../../common/utils/hooks/useSyncFlyoutRouteParams'
import { useAccountId } from '@labrav/common'
import type { CloudMarketplace } from '../../../modules/productsListing/types'
import { useAuth } from '@labrav/react-components'
import { providerToAuthCloud } from '@labrav/common'
import { buildCppoAuditJson, getFilteredChannelOfferStages } from './helpers'
import type { ViewChannelOfferProps } from './types'

export type { ViewChannelOfferProps } from './types'

export const ViewChannelOffer: React.FC<ViewChannelOfferProps> = ({
  crmOpportunityId,
}) => {
  useSyncFlyoutRouteParams()

  const dispatch = useDispatch()

  const { classes } = useStyles()
  const navigate = useNavigate()

  const {
    authorizationId,
    channelOfferId,
    cloud,
    accountId: routeAccountId,
  } = useParams<{
    authorizationId?: string
    channelOfferId?: string
    cloud?: string
    accountId?: string
  }>()

  const fallbackAccountId = useAccountId({ product: 'flyout' }) || ''
  const accountId = routeAccountId || fallbackAccountId
  const cloudMarketplace = useCurrentCloudProvider()

  const channelPrivateOffer: ChannelPartnerPrivateOffer = useSelector(
    state =>
      state.channelPartnerPrivateOffers.currentChannelPartnerPrivateOffer[
        authorizationId || ''
      ]
  )
  const { loading: authLoading, canDo } = useAuth()
  const canWriteMarketplace = useMemo(
    () =>
      !authLoading &&
      canDo({
        cloud: providerToAuthCloud(cloudMarketplace.toLowerCase()),
        service: 'marketplace',
        action: 'create',
      }),
    [authLoading, canDo, cloudMarketplace]
  )

  const permissionMessageString =
    'This action is disabled because you have read-only access'

  useEffect(() => {
    if (accountId && authorizationId) {
      dispatch(
        getCurrentReceivedResaleAuthorization(accountId, authorizationId)
      )
    }
  }, [accountId, authorizationId])

  const { receivedResaleAuthorization: resellerAuthorization } = useSelector<{
    receivedResaleAuthorization: ReceivedResellerAuthorization
  }>(state => {
    const receivedResaleAuthorization =
      state.receivedResellerAuthorization.currentReceivedResellerAuthorizations[
        authorizationId!
      ]

    return {
      receivedResaleAuthorization,
    }
  })

  const offerStatus = channelPrivateOffer?.offerStatus
  const [openEditRecipient, setOpenEditRecipient] = useState(false)
  const [openEditStakeHolders, setOpenEditStakeholders] = useState(false)
  const handleCloseRecipients = () => setOpenEditRecipient(false)
  const handleCloseStakeholders = () => setOpenEditStakeholders(false)

  const isRecipientDisabled = [
    OfferStatus.PRIVATE_OFFER_FAILED,
    OfferStatus.PRIVATE_OFFER_EXPIRED,
    OfferStatus.PRIVATE_OFFER_RESTRCITED,
    OfferStatus.PRIVATE_OFFER_RESTRICTION_IN_PROGRESS,
  ].includes(offerStatus || OfferStatus.PRIVATE_OFFER_RESTRICTION_IN_PROGRESS)

  const isStakeHolderDisabled = [
    OfferStatus.PRIVATE_OFFER_FAILED,
    OfferStatus.PRIVATE_OFFER_EXPIRED,
    OfferStatus.PRIVATE_OFFER_RESTRCITED,
    OfferStatus.PRIVATE_OFFER_RESTRICTION_IN_PROGRESS,
  ].includes(offerStatus || OfferStatus.PRIVATE_OFFER_RESTRICTION_IN_PROGRESS)

  const hideRecipient = offerStatus === OfferStatus.PRIVATE_OFFER_ACCEPTED
  const currencySymbol = getCurrencySymbol(
    channelPrivateOffer?.currency as string
  )

  useEffect(() => {
    if (
      !crmOpportunityId &&
      channelOfferId &&
      authorizationId &&
      !isEmpty(accountId) &&
      !isEmpty(resellerAuthorization)
    ) {
      dispatch(
        getChannelPartnerPrivateOffer({
          cloudMarketplace: 'AWS',
          partnerId: accountId,
          resellerAuthId: authorizationId,
          channelOfferId,
        })
      )
    }
  }, [
    accountId,
    channelOfferId,
    authorizationId,
    dispatch,
    resellerAuthorization,
  ])

  const handleSubmitRecipient = async (
    offerRecipientsToSend: OfferRecipients[]
  ) => {
    await dispatch(
      updateChannelPartnerPrivateOffer(
        channelOfferId || '',
        {
          offerRecipients: offerRecipientsToSend,
        },
        undefined,
        false,
        handleCloseRecipients,
        authorizationId,
        accountId,
        (cloud as CloudMarketplace) || cloudMarketplace
      )
    )
  }

  const handleSubmitStakeholders = async (
    offerStakeholders: OfferStakeholders[],
    customOfferStakeholders: OfferStakeholders[]
  ) => {
    const offerData = {
      offerStakeholders: [
        ...offerStakeholders.map(stakeholder => ({
          firstName: stakeholder.firstName,
          lastName: stakeholder.lastName,
          email: stakeholder.email,
        })),
        ...customOfferStakeholders.map(stakeholder => ({
          firstName: stakeholder.firstName || 'User',
          lastName: stakeholder.lastName || 'User',
          email: stakeholder.email,
        })),
      ],
    }

    await dispatch(
      updateChannelPartnerPrivateOffer(
        channelOfferId || '',
        offerData,
        undefined,
        false,
        handleCloseStakeholders,
        authorizationId,
        accountId,
        (cloud as CloudMarketplace) || cloudMarketplace
      )
    )
  }

  const metaData = channelPrivateOffer?.metaData || []
  const hasOfferMetaData = !!metaData.length

  const auditJson = buildCppoAuditJson(channelPrivateOffer)

  const statuses = () => {
    return getFilteredChannelOfferStages(
      auditJson,
      channelPrivateOffer.offerStatus as OfferStatus
    )
  }
  const isStakeholderActionDisabled =
    !canWriteMarketplace || isStakeHolderDisabled

  if (isEmpty(resellerAuthorization) || isEmpty(channelPrivateOffer)) {
    return <LoadingLogo />
  }

  return (
    <div data-testid={'channel-offer-details'}>
      <div className={classes.headerContainer}>
        <div className={classes.appTitleHeading}>
          <div
            className={classes.appBackIcon}
            data-testid="back-button"
            onClick={() => navigate(-1)}
          >
            <FontAwesomeIcon
              icon={faArrowLeft}
              style={{
                width: '24px',
                height: '24px',
                fontSize: '24px',
                padding: '0px 12px 0px 0px',
                color: '#1942C5',
              }}
            />
          </div>
          <Typography
            variant="h1"
            data-testid="heading"
            className={classes.appTitleHeadingText}
          >
            <ShortenedNameV2
              name={channelPrivateOffer?.offerName || ''}
              length={41}
            ></ShortenedNameV2>
          </Typography>
        </div>
        <SelectedCloudIcon />
      </div>
      <div className={classes.root}>
        <Grid container spacing={4}>
          <Grid
            className={classes.leftContainer}
            size={{
              sm: 12,
              md: 8,
              lg: 8,
              xl: 9,
            }}
          >
            {channelPrivateOffer && (
              <ChannelOfferHeader
                channelPrivateOffer={
                  channelPrivateOffer as ChannelPartnerPrivateOffer
                }
                planType={PlanType.SAAS_CONTRACT}
                crmOpportunityId={crmOpportunityId}
                resaleAuthorizationId={authorizationId}
              />
            )}
            <div data-testid="channel-offer-details-section">
              <ChannelOfferDetails
                channelOffer={channelPrivateOffer as ChannelPartnerPrivateOffer}
                resellerAuthorization={
                  resellerAuthorization as ReceivedResellerAuthorization
                }
              />
            </div>
            <div data-testid="channel-offer-dimenstion-details-section">
              <ChannelOfferDimensionDetails
                channelOffer={channelPrivateOffer as ChannelPartnerPrivateOffer}
                resellerAuthorization={
                  resellerAuthorization as ReceivedResellerAuthorization
                }
                currencySymbol={currencySymbol}
              />
            </div>
            <div data-testid="channel-offer-eula-section">
              <ChannelOfferEula
                channelOffer={channelPrivateOffer as ChannelPartnerPrivateOffer}
                resellerAuthorization={
                  resellerAuthorization as ReceivedResellerAuthorization
                }
              />
            </div>
            <div data-testid="channel-offer-metadata-section">
              {hasOfferMetaData && (
                <SectionWrapper
                  heading="Custom metadata"
                  icon={faTable}
                  children={
                    <div className={classes.metadata}>
                      <Grid container spacing={3}>
                        {metaData?.map(metaDataItem => (
                          <DetailInfo
                            title={metaDataItem?.key}
                            value={metaDataItem?.value || ''}
                          />
                        ))}
                      </Grid>
                    </div>
                  }
                />
              )}
            </div>
          </Grid>
          <Grid
            data-testid="cppo-stages"
            className={classes.rightContainer}
            size={{
              sm: 12,
              md: 4,
              lg: 4,
              xl: 3,
            }}
          >
            {!isEmpty(channelPrivateOffer?.auditJson) &&
              !channelPrivateOffer?.draft && (
                <StatusListing
                  statuses={statuses() as StatusOfferItem[]}
                  title={'Offer stage'}
                  titleIcon={faListCheck}
                  useBuiltinStyles={false}
                />
              )}
            <ViewRecipients
              offerRecipients={channelPrivateOffer?.offerRecipients || []}
              setOpenEditRecipient={setOpenEditRecipient}
              openEditRecipient={openEditRecipient}
            />
            <ViewStakeholders
              setOpenEditStakeholders={setOpenEditStakeholders}
              openEditStakeHolders={openEditStakeHolders}
              offerStakeholders={channelPrivateOffer?.offerStakeholders || []}
              customOfferStakeholders={
                channelPrivateOffer?.customOfferStakeholders || []
              }
            />
          </Grid>
        </Grid>
      </div>
      <ModalConfirmV2
        title={'Edit recipients'}
        content={
          <EditRecipients
            offerId={channelPrivateOffer?.awsAccountId || ''}
            offerRecipients={channelPrivateOffer?.offerRecipients || []}
            handleCloseRecipients={handleCloseRecipients}
            handleSubmit={handleSubmitRecipient}
          />
        }
        hideCancelButton={true}
        open={openEditRecipient}
        hideAcceptButton={true}
        onAccept={noop}
        onDecline={handleCloseRecipients}
        variant={ModalSizeVariants.large}
        icon={<FontAwesomeIcon style={{ height: '14px' }} icon={faShelves} />}
      />
      <ModalConfirmV2
        title={'Edit Stakeholders'}
        content={
          <EditStakeholders
            offerStakeholders={channelPrivateOffer?.offerStakeholders || []}
            customOfferStakeholders={
              channelPrivateOffer?.customOfferStakeholders || []
            }
            handleCloseStakeholders={handleCloseStakeholders}
            handleSubmit={handleSubmitStakeholders}
          />
        }
        hideCancelButton={true}
        open={openEditStakeHolders}
        hideAcceptButton={true}
        onAccept={noop}
        onDecline={handleCloseStakeholders}
        variant={ModalSizeVariants.large}
        icon={<FontAwesomeIcon style={{ height: '14px' }} icon={faShelves} />}
      />
    </div>
  )
}
